// frontend/modules/customer-category/components/onboarding/CategorySelectionStep.tsx
import React, { useState, useEffect } from 'react';
import {
    Card,
    Radio,
    Space,
    Typography,
    Row,
    Col,
    Empty,
    Spin,
    Alert
} from 'antd';
import { CheckCircleFilled, UserOutlined, TeamOutlined, BankOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { CustomerOnboardingData } from '../../types';
import {CustomerCategoryDTO, UserCategoryAccessDTO} from '@/shared/api/model';

const { Text, Paragraph } = Typography;

interface CategorySelectionStepProps {
    userCategoryAccess: UserCategoryAccessDTO | null;
    formData: CustomerOnboardingData;
    onUpdateFormData: (data: Partial<CustomerOnboardingData>) => void;
}

const CategorySelectionStep: React.FC<CategorySelectionStepProps> = ({
                                                                         userCategoryAccess,
                                                                         formData,
                                                                         onUpdateFormData
                                                                     }) => {
    const { dark } = useTheme();
    const [selectedOption, setSelectedOption] = useState<'category' | 'freestyle'>(
        formData.isFreestyle ? 'freestyle' : 'category'
    );

    // Set default category if available and none is selected
    useEffect(() => {
        if (userCategoryAccess && userCategoryAccess.defaultCategory && !formData.categoryId) {
            onUpdateFormData({
                categoryId: userCategoryAccess.defaultCategory.id,
                isFreestyle: false
            });
            setSelectedOption('category');
        }
    }, [userCategoryAccess, formData.categoryId, onUpdateFormData]);

    const handleOptionChange = (e: any) => {
        const option = e.target.value;
        setSelectedOption(option);
        onUpdateFormData({
            isFreestyle: option === 'freestyle',
            categoryId: option === 'freestyle' ? undefined : formData.categoryId
        });
    };

    const handleCategorySelect = (category: CustomerCategoryDTO) => {
        onUpdateFormData({
            categoryId: category.id,
            isFreestyle: false
        });
    };

    if (!userCategoryAccess) {
        return <Spin tip="Loading available categories..." />;
    }

    return (
        <div className="space-y-4">
            <Paragraph className={dark ? 'text-gray-300' : 'text-gray-700'}>
                Choose a customer category or use freestyle mode to customize the onboarding process.
            </Paragraph>

            <Radio.Group
                value={selectedOption}
                onChange={handleOptionChange}
                className="mb-6 w-full"
            >
                <Space direction="vertical" className="w-full">
                    <Radio value="category">
                        <Text strong>Use Predefined Category</Text>
                        <Paragraph type="secondary" className="ml-6">
                            Select from predefined customer categories with preset products and field requirements.
                        </Paragraph>
                    </Radio>

                    {userCategoryAccess.canUseFreestyle && (
                        <Radio value="freestyle">
                            <Text strong>Freestyle Mode</Text>
                            <Paragraph type="secondary" className="ml-6">
                                Customize fields and products manually during the onboarding process.
                            </Paragraph>
                        </Radio>
                    )}
                </Space>
            </Radio.Group>

            {selectedOption === 'category' && (
                <>
                    {!userCategoryAccess.availableCategories || userCategoryAccess.availableCategories.length === 0 ? (
                        <Empty description="No customer categories available" />
                    ) : (
                        <Row gutter={[16, 16]}>
                            {userCategoryAccess.availableCategories?.map(category => (
                                <Col xs={24} sm={12} md={8} key={category.id}>
                                    <Card
                                        hoverable
                                        className={`h-full ${formData.categoryId === category.id ?
                                            (dark ? 'border-blue-400 bg-blue-900/20' : 'border-blue-500 bg-blue-50') :
                                            ''}`}
                                        onClick={() => handleCategorySelect(category)}
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <Text strong className="text-lg">{category.name}</Text>
                                                <Paragraph
                                                    ellipsis={{ rows: 2 }}
                                                    className="text-sm text-gray-500 dark:text-gray-400"
                                                >
                                                    {category.description}
                                                </Paragraph>
                                                <div className="mt-2">
                                                    <Text type="secondary" className="text-xs">
                                                        {(category.products || []).length} products
                                                    </Text>
                                                </div>
                                            </div>
                                            {formData.categoryId === category.id && (
                                                <CheckCircleFilled
                                                    className="text-blue-500 text-xl"
                                                />
                                            )}
                                        </div>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    )}
                </>
            )}

            {selectedOption === 'freestyle' && (
                <Alert
                    message="Freestyle Mode"
                    description="You've selected freestyle mode. You'll need to manually configure all fields and products in the following steps."
                    type="info"
                    showIcon
                    className="mt-4"
                />
            )}
        </div>
    );
};

export default CategorySelectionStep;